import React, { useState } from 'react';
import { StarIcon, PlayIcon, LightbulbIcon, HandIcon, ShieldCheckIcon, XIcon } from 'lucide-react';
import { AuthorSignature } from '../components/AuthorSignature';
type ModalType = 'terms' | 'privacy' | 'contact' | null;
export function StoreWaterPage() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <div className="min-h-screen w-full bg-[#f8f7f4] font-sans text-[#1e293b] selection:bg-[#b91c1c] selection:text-white" data-id="element-1226">
      {/* 1. Top Nav Bar */}
      <header className="bg-[#002868] text-white w-full" data-id="element-1227">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-12 flex items-center justify-between" data-id="element-1228">
          <div className="flex items-center gap-2 font-bold text-lg tracking-tight" data-id="element-1229">
            <StarIcon className="w-5 h-5 fill-current text-[#b91c1c]" data-id="element-1230" />
            <span data-id="element-1231">PatriotsInformer</span>
          </div>
        </div>
      </header>

      {/* 2. Breadcrumb Bar */}
      <div className="bg-white border-b border-gray-200 w-full" data-id="element-1232"></div>

      {/* Main Content Area */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12 space-y-12" style={{
      paddingTop: '2px'
    }} data-id="element-1233">
        {/* 3. Article Section */}
        <article className="space-y-6" data-id="element-1234">
          {/* Title & Meta */}
          <div className="space-y-2" data-id="element-1235">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-[#0f172a] tracking-tight leading-tight" data-id="element-1236">
              The RIGHT Way to Store Water Long-Term for Emergencies!
            </h1>
          </div>

          {/* Author Bio */}
          <div className="flex flex-row items-center gap-4 sm:gap-6" data-id="element-1237">
            <img src="https://cdn.magicpatterns.com/uploads/qCiNdfGtT6ARUfiGnS7VJm/jack-turner-avatar-2.jpg" alt="Jack Turner" className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover border-2 border-gray-200 shadow-sm flex-shrink-0" data-id="element-1238" />
            <div className="text-left space-y-1" data-id="element-1239">
              <h3 className="font-bold text-[#0f172a] text-lg" data-id="element-1240">Jack Turner</h3>
              <p className="text-gray-500 text-sm" data-id="element-1241">
                Head of research at PatriotsInformer.com
              </p>
              <p className="text-gray-400 text-xs uppercase tracking-wide pt-1" data-id="element-1242">
                May 5, 2026 · 4 min read
              </p>
            </div>
          </div>

          {/* Video Embed */}
          <div className="relative w-full aspect-video bg-black rounded-lg overflow-hidden shadow-xl" data-id="element-1243">
            <iframe src="https://www.youtube.com/embed/CVULQxujrAk?modestbranding=1&rel=0&disablekb=1&fs=0&iv_load_policy=3&playsinline=1&controls=0" title="Video" className="w-full h-full" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" referrerPolicy="strict-origin-when-cross-origin" sandbox="allow-scripts allow-same-origin allow-presentation" data-id="element-1244" />
          </div>

          {/* Article Body */}
          <div className="space-y-6 text-lg text-[#334155] leading-relaxed" data-id="element-1245">
            <p data-id="element-1246">
              Storing water is one of the{' '}
              <b data-id="element-1247">smartest things you can do for your family's preparedness.</b>{' '}
              But storing it is only half the equation.
            </p>
            <p data-id="element-1248">
              Because here's the thing most people don't think about until it's
              too late: containers run out. Rotation schedules get forgotten.
            </p>
            <p data-id="element-1249">
              And in a real extended emergency, even a{' '}
              <b data-id="element-1250">well-stocked supply has a hard limit.</b>
            </p>

            <p data-id="element-1251">
              The people who are truly prepared aren't just storing water.
              <b data-id="element-1252">They're building a source that never runs out,</b> one that
              doesn't depend on how many containers they have in the garage or
              whether they remembered to rotate last spring
            </p>
            <p data-id="element-1253">
              That's the next level. And once you see{' '}
              <b data-id="element-1254">how simple it actually is,</b>
              you'll never look at water storage the same way again.
            </p>
          </div>

          {/* CTA Callout Box */}
          <div className="mt-12 border-4 border-dashed border-[#b91c1c] bg-yellow-50 p-6 sm:p-8 rounded-lg relative" data-id="element-1255">
            <div className="space-y-4" data-id="element-1256">
              <div className="flex items-start gap-3" data-id="element-1257">
                <h3 className="text-xl font-bold text-[#0f172a]" data-id="element-1258">
                  Want to go beyond storage and build your own unlimited water
                  supply?
                </h3>
              </div>

              <p className="text-[#334155] text-lg leading-relaxed" data-id="element-1259">
                This step-by-step guide shows you exactly how to create a device
                that pulls clean, drinkable water straight from the air, no
                containers, no rotation, no grid required.
              </p>

              <div className="pt-2" data-id="element-1260">
                <a href="https://trk.adsdomredir.com/click/1" className="inline-flex items-center font-bold underline decoration-2 underline-offset-4 text-[20px] text-[#0000ff] gap-[1px]" data-id="element-1261">
                  👉 Watch how it's done here
                </a>
              </div>
            </div>
          </div>
        </article>

        {/* 4. 'You Might Also Like' Section */}
        <section className="space-y-8 pt-8 border-t border-gray-200" data-id="element-1262">
          <h2 className="text-3xl font-bold text-[#0f172a]" data-id="element-1263">
            You Might Also Like:
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8" data-id="element-1264">
            {/* Card 1 */}
            <div className="group cursor-pointer space-y-3" data-id="element-1265">
              <a href="https://trk.adsdomredir.com/click/2" className="block aspect-[4/3] overflow-hidden rounded-lg shadow-md" data-id="element-1266">
                <img src="https://cdn.magicpatterns.com/uploads/tfBxRA9s5qgvhpKFp12h5o/unnamed_(7).jpg" alt="The Lost SuperFoods book" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-1267" />
              </a>
              <h3 className="font-bold text-lg text-[#0f172a] leading-tight group-hover:text-[#002868] transition-colors" data-id="element-1268">
                <a href="https://trk.adsdomredir.com/click/2" data-id="element-1269">
                  Superfoods That You Can Store Without Refrigeration for Years
                </a>
              </h3>
            </div>

            {/* Card 2 */}
            <div className="group cursor-pointer space-y-3" data-id="element-1270">
              <a href="https://trk.adsdomredir.com/click/3" className="block aspect-[4/3] overflow-hidden rounded-lg shadow-md" data-id="element-1271">
                <img src="https://cdn.magicpatterns.com/uploads/aeW4oPiWoeR5Wzq6FuUxBZ/new5notext.png" alt="Vegetable garden" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-1272" />
              </a>
              <h3 className="font-bold text-lg text-[#0f172a] leading-tight group-hover:text-[#002868] transition-colors" data-id="element-1273">
                <a href="https://trk.adsdomredir.com/click/3" data-id="element-1274">
                  The Underground Energy Method They Don't Want You To Know
                  About
                </a>
              </h3>
            </div>

            {/* Card 3 */}
            <div className="group cursor-pointer space-y-3" data-id="element-1275">
              <a href="https://trk.adsdomredir.com/click/4" className="block aspect-[4/3] overflow-hidden rounded-lg shadow-md" data-id="element-1276">
                <img src="https://cdn.magicpatterns.com/uploads/7ksskNxA12GTBLDP2Kydn9/img18.png" alt="Man holding tools" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-1277" />
              </a>
              <h3 className="font-bold text-lg text-[#0f172a] leading-tight group-hover:text-[#002868] transition-colors" data-id="element-1278">
                <a href="https://trk.adsdomredir.com/click/4" data-id="element-1279">
                  Nature-Inspired 3D Solar Sphere Generates Energy On Demand
                  (Watch Video)
                </a>
              </h3>
            </div>
          </div>

          <AuthorSignature data-id="element-1280" />
        </section>
      </main>

      {/* 5. Footer */}
      <footer className="bg-white border-t border-gray-200 mt-12 py-12" data-id="element-1281">
        <div className="max-w-4xl mx-auto px-4 text-center space-y-6" data-id="element-1282">
          <div className="flex items-center justify-center gap-2 text-[#b91c1c]" data-id="element-1283">
            <div className="relative w-10 h-10" data-id="element-1284">
              <StarIcon className="w-10 h-10 fill-current text-[#002868]" data-id="element-1285" />
              <StarIcon className="w-4 h-4 fill-current text-white absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" data-id="element-1286" />
            </div>
          </div>

          <div className="text-xs text-gray-500 space-y-4 max-w-2xl mx-auto leading-relaxed" data-id="element-1287">
            <p data-id="element-1288">
              © 2018-2026 PatriotsInformer Media, LLC | All Rights Reserved |
              63 N. Burritt - Buffalo, WY 82834 United States
            </p>
            <p className="space-x-2" data-id="element-1289">
              <button onClick={() => setActiveModal('terms')} className="underline hover:text-[#002868] cursor-pointer" data-id="element-1290">
                Terms & Conditions
              </button>
              <span data-id="element-1291">|</span>
              <button onClick={() => setActiveModal('privacy')} className="underline hover:text-[#002868] cursor-pointer" data-id="element-1292">
                Privacy Policy
              </button>
              <span data-id="element-1293">|</span>
              <button onClick={() => setActiveModal('contact')} className="underline hover:text-[#002868] cursor-pointer" data-id="element-1294">
                Contact Us
              </button>
            </p>
            <p data-id="element-1295">
              No part of this report may be reproduced or placed on any
              electronic medium without written permission from the publisher.
              Information contained herein is obtained from sources believed to
              be reliable, but its accuracy cannot be guaranteed.
            </p>
          </div>
        </div>
      </footer>

      {/* Modals */}
      {activeModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setActiveModal(null)} data-id="element-1296">
          <div className="absolute inset-0 bg-black/50" data-id="element-1297" />
          <div className="relative bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-id="element-1298">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between" data-id="element-1299">
              <h2 className="text-lg font-bold text-[#0f172a]" data-id="element-1300">
                {activeModal === 'terms' && 'Terms & Conditions'}
                {activeModal === 'privacy' && 'Privacy Policy'}
                {activeModal === 'contact' && 'Contact Us'}
              </h2>
              <button onClick={() => setActiveModal(null)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors" data-id="element-1301">
                <XIcon className="w-5 h-5 text-gray-500" data-id="element-1302" />
              </button>
            </div>

            <div className="px-6 py-6 text-sm text-[#334155] leading-relaxed space-y-4" data-id="element-1303">
              {activeModal === 'terms' && <>
                  <p className="font-semibold text-[#0f172a]" data-id="element-1304">
                    Last Updated: February 2026
                  </p>
                  <p data-id="element-1305">
                    Welcome to PatriotsInformer.com. By accessing or using our
                    website, you agree to be bound by these Terms & Conditions.
                    Please read them carefully before using our services.
                  </p>
                  <h3 className="font-bold text-[#0f172a] pt-2" data-id="element-1306">
                    1. Acceptance of Terms
                  </h3>
                  <p data-id="element-1307">
                    By accessing this website, you acknowledge that you have
                    read, understood, and agree to be bound by these Terms &
                    Conditions. If you do not agree, please do not use this
                    website.
                  </p>
                  <h3 className="font-bold text-[#0f172a] pt-2" data-id="element-1308">
                    2. Use of Content
                  </h3>
                  <p data-id="element-1309">
                    All content on this website, including text, graphics,
                    images, and other material, is for informational purposes
                    only. No part of this report may be reproduced or placed on
                    any electronic medium without written permission from the
                    publisher.
                  </p>
                  <h3 className="font-bold text-[#0f172a] pt-2" data-id="element-1310">
                    3. Disclaimer
                  </h3>
                  <p data-id="element-1311">
                    Information contained herein is obtained from sources
                    believed to be reliable, but its accuracy cannot be
                    guaranteed. The content is not intended as professional
                    advice. Always consult qualified professionals for specific
                    guidance.
                  </p>
                  <h3 className="font-bold text-[#0f172a] pt-2" data-id="element-1312">
                    4. Limitation of Liability
                  </h3>
                  <p data-id="element-1313">
                    PatriotsInformer Media, LLC shall not be liable for any
                    damages arising from the use or inability to use the
                    materials on this website.
                  </p>
                  <h3 className="font-bold text-[#0f172a] pt-2" data-id="element-1314">
                    5. Governing Law
                  </h3>
                  <p data-id="element-1315">
                    These terms shall be governed by and construed in accordance
                    with the laws of the State of Texas, without regard to its
                    conflict of law provisions.
                  </p>
                  <p className="pt-2 text-xs text-gray-400" data-id="element-1316">
                    © 2018-2026 PatriotsInformer Media, LLC. All Rights
                    Reserved.
                  </p>
                </>}

              {activeModal === 'privacy' && <>
                  <p className="font-semibold text-[#0f172a]" data-id="element-1317">
                    Last Updated: February 2026
                  </p>
                  <p data-id="element-1318">
                    PatriotsInformer Media, LLC ("we," "us," or "our") is
                    committed to protecting your privacy. This Privacy Policy
                    explains how we collect, use, and safeguard your
                    information.
                  </p>
                  <h3 className="font-bold text-[#0f172a] pt-2" data-id="element-1319">
                    1. Information We Collect
                  </h3>
                  <p data-id="element-1320">
                    We may collect personal information such as your name and
                    email address when you voluntarily submit it through our
                    opt-in forms. We also collect non-personal information such
                    as browser type, IP address, and pages visited.
                  </p>
                  <h3 className="font-bold text-[#0f172a] pt-2" data-id="element-1321">
                    2. How We Use Your Information
                  </h3>
                  <p data-id="element-1322">
                    We use the information we collect to send you the content
                    you requested, communicate updates and offers, improve our
                    website and services, and comply with legal obligations.
                  </p>
                  <h3 className="font-bold text-[#0f172a] pt-2" data-id="element-1323">
                    3. Email Communications
                  </h3>
                  <p data-id="element-1324">
                    By providing your email address, you consent to receive
                    emails from us. You can unsubscribe at any time by clicking
                    the "unsubscribe" link at the bottom of any email.
                  </p>
                  <h3 className="font-bold text-[#0f172a] pt-2" data-id="element-1325">
                    4. Third-Party Sharing
                  </h3>
                  <p data-id="element-1326">
                    We do not sell, trade, or rent your personal information to
                    third parties. We may share information with trusted service
                    providers who assist us in operating our website.
                  </p>
                  <h3 className="font-bold text-[#0f172a] pt-2" data-id="element-1327">
                    5. Data Security
                  </h3>
                  <p data-id="element-1328">
                    We implement reasonable security measures to protect your
                    personal information. However, no method of transmission
                    over the Internet is 100% secure.
                  </p>
                  <p className="pt-2 text-xs text-gray-400" data-id="element-1329">
                    © 2018-2026 PatriotsInformer Media, LLC. All Rights
                    Reserved.
                  </p>
                </>}

              {activeModal === 'contact' && <>
                  <p data-id="element-1330">
                    We'd love to hear from you. Feel free to reach out using the
                    information below:
                  </p>
                  <div className="bg-[#f8f7f4] rounded-lg p-5 space-y-3 border border-gray-200" data-id="element-1331">
                    <h3 className="font-bold text-[#0f172a]" data-id="element-1332">
                      PatriotsInformer Media, LLC
                    </h3>
                    <p data-id="element-1333">
                      10160 Highway 242 Ste 800
                      <br data-id="element-1334" />
                      PMB 1089
                      <br data-id="element-1335" />
                      Conroe, TX 77385
                    </p>
                    <p data-id="element-1336">
                      <span className="font-semibold text-[#0f172a]" data-id="element-1337">
                        Email:
                      </span>{' '}
                      <a href="mailto:support@patriotsinformer.com" className="text-[#002868] underline" data-id="element-1338">
                        support@patriotsinformer.com
                      </a>
                    </p>
                  </div>
                  <p className="pt-2 text-xs text-gray-400" data-id="element-1339">
                    © 2018-2026 PatriotsInformer Media, LLC. All Rights
                    Reserved.
                  </p>
                </>}
            </div>
          </div>
        </div>}
    </div>;
}